/**
 * ExecutionLogPane - monospace scrolling pane rendering an execution's
 * log entries (ts, [status], resource, rows/error). Extracted from
 * RunningProgressView so completed/failed executions can show their
 * run log too (#785).
 */
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { ExecutionLogEntry } from '@/types/connectors';

interface Props {
  log: ExecutionLogEntry[] | null | undefined;
  title: string;
  emptyText?: string;
}

export function ExecutionLogPane({ log, title, emptyText = 'Waiting for first resource…' }: Props) {
  const entries = log ?? [];
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase mb-2">{title}</div>
      <div
        className="rounded-md bg-muted text-foreground font-mono text-[11px] leading-5 p-3 max-h-64 overflow-y-auto"
        data-testid="live-log"
      >
        {entries.length === 0 ? (
          <div className="text-muted-foreground">{emptyText}</div>
        ) : (
          entries.map((entry, idx) => (
            <div key={idx} className="whitespace-pre-wrap">
              <span className="text-muted-foreground">{format(new Date(entry.ts), 'h:mm:ss a')}</span>{' '}
              <span
                className={cn(
                  entry.status === 'success' && 'text-success',
                  entry.status === 'failed' && 'text-destructive',
                  entry.status === 'started' && 'text-info',
                )}
              >
                [{entry.status}]
              </span>{' '}
              <span>{entry.resource}</span>
              {entry.customer_id ? ` · acct ${entry.customer_id}` : ''}
              {entry.rows !== null && entry.rows !== undefined
                ? ` · ${entry.rows.toLocaleString()} rows`
                : ''}
              {entry.error ? ` — ${entry.error}` : ''}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
